import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import TableSkeleton from '../../../components/TableSkeleton';
import ExportButton from '../components/ExportButton';
import styles from '../OrderRulesReport.styles';

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
}

interface MaxPotentialDisplayRow {
  rowType: 'parent' | 'child' | 'no-layer';
  key: string;
  region: string;
  market: string;
  buildLocation: string;
  skuSlots: string;
  lastUpdatedAt: string;
  layerPerformancePct: string;
  layerPerformanceValue: number;
  maxPlanPct: string;
  optimizedPlanPct: string;
  maxPotentialPct: string;
  maxPotExecPct: string;
  estimatedLaborSavings: string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  siteType?: 'build' | 'cross-dock';
}

interface MetricCard {
  key: string;
  label: string;
  value: string;
  subLabel: string;
  tip: string;
}

interface MaxPotentialTabProps {
  loading: boolean;
  metricCards: MetricCard[];
  showSitesWithoutLayerLine: boolean;
  onToggleSitesWithoutLayerLine: () => void;
  rows: MaxPotentialDisplayRow[];
  onToggleRow: (key: string) => void;
  /** Server-side sort state — column key + direction currently applied via the API. */
  sort: { col: string | null; dir: 'asc' | 'desc' | null };
  /** Fires the sortBy/sortOrder API call for the Max Potential Execution table. */
  onSort: (colKey: string) => void;
  /** Pagination bar, rendered directly under the table. */
  pagination?: React.ReactNode;
  /** Triggers the export API for this tab. */
  onExport: () => Promise<void>;
}

const maxPotentialExecutionColumns: TableColumn[] = [
  { key: 'region', label: 'Region', minWidth: 140, align: 'left' },
  { key: 'market', label: 'Market', minWidth: 210, align: 'left' },
  { key: 'buildLocation', label: 'Build Location', minWidth: 270, align: 'left' },
  { key: 'skuSlots', label: 'SKU Slots', minWidth: 110, align: 'center' },
  { key: 'lastUpdatedAt', label: 'Last Update to Order Rules', minWidth: 235, align: 'center' },
  { key: 'layerPerformancePct', label: 'Layer Performance', minWidth: 165, align: 'center' },
  { key: 'maxPlanPct', label: 'Max Plan', minWidth: 120, align: 'center' },
  { key: 'optimizedPlanPct', label: 'Optimized Plan', minWidth: 145, align: 'center' },
  { key: 'maxPotentialPct', label: 'Max Potential', minWidth: 145, align: 'center' },
  { key: 'maxPotExecPct', label: 'Max Potential Execution', minWidth: 200, align: 'center' },
  { key: 'estimatedLaborSavings', label: 'Est. Cost Savings ($/wk)', minWidth: 210, align: 'center' },
];

const maxPotentialHeaderTooltips: Record<string, string> = {
  skuSlots: 'Layer line slots from the iCAM survey minus 3 build pallet spots. No layer line sites are modeled at 10.',
  lastUpdatedAt: 'This is the last time anything was updated in sku rule enrollment.',
  layerPerformancePct: 'Actual cases picked in full layers vs total layer plus bulk volume, from Voice Pick at the build location.',
  maxPlanPct: 'Best achievable with SKUs already set up in Voice Pick SKU Maintenance, using top N SKUs by layer line slots.',
  optimizedPlanPct: 'Best achievable if all eligible SKUs were correctly configured for layer picking, including cross dock and dispatch timing corrections.',
  maxPotentialPct: 'Best achievable if all High and Medium confidence recommendations were applied and order quantities re-rounded to layer multiples.',
  maxPotExecPct: 'Layer Performance as a percentage of Max Potential — how much of the available layer picking opportunity is currently being captured.',
  estimatedLaborSavings: 'Estimated weekly cost savings in dollars if the site reaches its max potential.',
};

const ladderSteps = [
  {
    title: 'Layer Performance',
    body: 'Measured as the percentage of cases picked in full layers compared to the total bulk and layer volume.',
  },
  {
    title: 'Max Plan',
    body: 'Represents the highest achievable layer performance using only SKUs currently configured in Voice Pick SKU Maintenance. This assumes optimal assignment of the top N SKUs to layer line slots (calculated as iCAM capacity minus three build pallet positions).',
  },
  {
    title: 'Optimized Plan',
    body: 'Represents the highest achievable layer performance if all eligible SKUs were properly configured for layer picking. This scenario also assumes optimal assignment of the top N SKUs to layer line slots (iCAM capacity minus three build pallet positions).',
  },
  {
    title: 'Max Potential',
    body: 'Represents the highest possible layer performance if all eligible SKUs were correctly configured for layer picking and all High and Medium confidence rule recommendations were implemented. This also assumes optimal assignment of the top N SKUs to layer line slots (iCAM capacity minus three build pallet positions).',
  },
];


// Sub-component that owns the shimmer animation so hooks are always called unconditionally
const KpiCardRow: React.FC<{
  loading: boolean;
  metricCards: MetricCard[];
  hoveredMetricCardKey: string | null;
  setHoveredMetricCardKey: (key: string | null) => void;
}> = ({ loading, metricCards, hoveredMetricCardKey, setHoveredMetricCardKey }) => {
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!loading) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [loading, shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });

  return (
    <View style={[styles.kpiRow, styles.maxPotentialMetricRow]}>
      {metricCards.map((metric) => (
        <Pressable
          key={metric.key}
          style={[
            styles.kpiCard,
            Platform.OS === 'web' && hoveredMetricCardKey === metric.key && styles.kpiCardHover,
          ]}
          onHoverIn={Platform.OS === 'web' ? () => setHoveredMetricCardKey(metric.key) : undefined}
          onHoverOut={Platform.OS === 'web' ? () => setHoveredMetricCardKey(null) : undefined}
        >
          <View style={styles.maxPotentialMetricLabelRow}>
            <Text style={styles.kpiLabel}>{metric.label}</Text>
            <DLOTooltip content={metric.tip} trigger="hover" position="bottom" maxWidth={300}>
              <View style={styles.maxPotentialInfoBtn}>
                <MaterialIcons name="info-outline" size={14} color="#6B7280" />
              </View>
            </DLOTooltip>
          </View>
          {loading ? (
            <>
              <Animated.View style={{ marginTop: 10, height: 28, width: '60%', borderRadius: 6, backgroundColor: '#E6EEF7', opacity }} />
              <Animated.View style={{ marginTop: 8, height: 12, width: '80%', borderRadius: 6, backgroundColor: '#F1F5F9', opacity }} />
            </>
          ) : (
            <>
              <Text style={styles.kpiValue}>{metric.value}</Text>
              <Text style={styles.kpiSubLabel}>{metric.subLabel}</Text>
            </>
          )}
        </Pressable>
      ))}
    </View>
  );
};


/** Fixed row height and max visible rows before the body scrolls vertically — keeps the sticky header pattern consistent across tabs. */
const ROW_HEIGHT = 44;
const MAX_VISIBLE_ROWS = 12;

const MaxPotentialTab: React.FC<MaxPotentialTabProps> = ({
  loading,
  metricCards,
  showSitesWithoutLayerLine,
  onToggleSitesWithoutLayerLine,
  rows,
  onToggleRow,
  sort: maxSort,
  onSort: handleMaxSort,
  pagination,
  onExport,
}) => {
  const [hoveredMetricCardKey, setHoveredMetricCardKey] = useState<string | null>(null);
  const [isLadderExpanded, setIsLadderExpanded] = useState(false);

  // Rows arrive already sorted by the backend (sortBy/sortOrder in the API
  // request), so the table renders them as-is.
  const sortedRows = rows;

  // ── Scroll sync refs (header \u2194 body, horizontal) so the column header stays
  // pinned while the body scrolls vertically inside its fixed-height box.
  const headerScrollRef = useRef<ScrollView>(null);
  const bodyScrollRef = useRef<ScrollView>(null);
  const isHeaderScrolling = useRef(false);
  const isBodyScrolling = useRef(false);

  const onHeaderHorizontalScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isBodyScrolling.current) return;
    isHeaderScrolling.current = true;
    bodyScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isHeaderScrolling.current = false;
  }, []);

  const onBodyHorizontalScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isHeaderScrolling.current) return;
    isBodyScrolling.current = true;
    headerScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isBodyScrolling.current = false;
  }, []);

  const maxPotentialTableWidth = useMemo(
    () => maxPotentialExecutionColumns.reduce((acc, col) => acc + (col.minWidth ?? 120), 0),
    [],
  );
  const maxPotentialContentWidthStyle = useMemo(() => ({ width: maxPotentialTableWidth }), [maxPotentialTableWidth]);
  const maxPotentialBodyHeight = useMemo(
    () => Math.min(sortedRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT,
    [sortedRows.length],
  );

  const getMaxPotentialItemLayout = useCallback(
    (_: any, index: number) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index }),
    [],
  );

  const renderMaxPotentialRow = useCallback(
    ({ item: row }: { item: MaxPotentialDisplayRow }) => (
      <View style={[styles.tableDataRow, row.rowType === 'child' && styles.maxPotentialChildRow]}>
        {maxPotentialExecutionColumns.map((column) => {
          const value = row[column.key as keyof MaxPotentialDisplayRow];

          return (
            <View
              key={`${row.key}-${column.key}`}
              style={[
                styles.tableDataCell,
                {
                  width: column.minWidth ?? 120,
                  minWidth: column.minWidth ?? 120,
                  maxWidth: column.minWidth ?? 120,
                  overflow: 'hidden',
                },
                column.align === 'right' && styles.alignRight,
                column.align === 'center' && styles.alignCenter,
              ]}
            >
              {column.key === 'buildLocation' ? (
                row.rowType === 'child' ? (
                  <View style={styles.maxPotentialChildLocationWrap}>
                    <View style={styles.maxPotentialChildTag}>
                      <Text style={styles.maxPotentialChildTagText}>{row.siteType === 'build' ? 'Build location' : 'Cross dock'}</Text>
                    </View>
                    <Text style={styles.tableDataText}>{row.buildLocation}</Text>
                  </View>
                ) : row.rowType === 'no-layer' ? (
                  <TouchableOpacity activeOpacity={0.75} onPress={() => {}}>
                    <Text style={styles.tableLinkText}>{row.buildLocation}</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.maxPotentialBuildLocationWrap}>
                    <TouchableOpacity
                      disabled={!row.isExpandable}
                      onPress={row.isExpandable ? () => onToggleRow(row.key) : undefined}
                      style={styles.maxPotentialRowToggleBtn}
                      activeOpacity={0.8}
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={15}
                        color="#5D6878"
                        style={!row.isExpandable ? styles.maxPotentialRowToggleIconDisabled : row.isExpanded ? styles.maxPotentialRowToggleIconOpen : null}
                      />
                    </TouchableOpacity>
                    <Text style={[styles.tableDataText, styles.tableDataTextStrong]}>{row.buildLocation}</Text>
                  </View>
                )
              ) : column.key === 'layerPerformancePct' ? (
                <Text
                  style={[
                    styles.tableDataText,
                    styles.tableDataTextStrong,
                    row.layerPerformanceValue >= 75 ? styles.maxPotentialPositiveText : styles.maxPotentialNegativeText,
                  ]}
                >
                  {String(value ?? '-')}
                </Text>
              ) : column.key === 'maxPotentialPct' ? (
                <Text style={[styles.tableDataText, styles.tableDataTextStrong]}>{String(value ?? '-')}</Text>
              ) : column.key === 'maxPotExecPct' ? (
                <Text style={[styles.tableDataText, styles.tableDataTextStrong]}>{String(value ?? '-')}</Text>
              ) : (
                <Text style={styles.tableDataText}>{String(value ?? '-')}</Text>
              )}
            </View>
          );
        })}
      </View>
    ),
    [onToggleRow],
  );

  return (
    <View style={styles.sectionGap}>
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <TouchableOpacity
            style={styles.maxPotentialLadderToggle}
            activeOpacity={0.75}
            onPress={() => setIsLadderExpanded((current) => !current)}
          >
            <Text style={styles.maxPotentialLadderToggleText}>
              {isLadderExpanded ? 'Hide how the ladder works' : 'Click here to see how the ladder works'}
            </Text>
            <MaterialIcons
              name={isLadderExpanded ? 'expand-less' : 'expand-more'}
              size={18}
              color="#005CBC"
            />
          </TouchableOpacity>
          {isLadderExpanded ? (
            <Text style={styles.cardSubTitle}>Each step recalculates layer performance under progressively better conditions</Text>
          ) : null}
        </View>
        {isLadderExpanded ? (
          <View style={styles.maxPotentialGuideLadder}>
            {ladderSteps.map((step, index) => (
              <View key={step.title} style={styles.maxPotentialGuideStep}>
                <Text style={styles.maxPotentialGuideStepTitle}>{step.title}</Text>
                <Text style={styles.maxPotentialGuideStepBody}>{step.body}</Text>
                {index < ladderSteps.length - 1 ? (
                  <MaterialIcons name="arrow-forward" size={16} color="#B4C0D2" style={styles.maxPotentialGuideArrow} />
                ) : null}
              </View>
            ))}
          </View>
        ) : null}
      </View>

      <KpiCardRow
        loading={loading}
        metricCards={metricCards}
        hoveredMetricCardKey={hoveredMetricCardKey}
        setHoveredMetricCardKey={setHoveredMetricCardKey}
      />

      <View style={[styles.card, styles.maxPotentialToggleCard]}>
        <View style={styles.maxPotentialToggleRow}>
          <TouchableOpacity
            style={[styles.maxPotentialSwitchTrack, showSitesWithoutLayerLine && styles.maxPotentialSwitchTrackOn]}
            activeOpacity={0.85}
            onPress={onToggleSitesWithoutLayerLine}
          >
            <View style={[styles.maxPotentialSwitchThumb, showSitesWithoutLayerLine && styles.maxPotentialSwitchThumbOn]} />
          </TouchableOpacity>
          <Text style={styles.maxPotentialToggleLabel}>Show Sites Without a Layer Line</Text>
          <Text style={styles.maxPotentialToggleDescription}>
            No layer line sites are modeled at 10 pick positions; estimated labor savings assume the site reaches max potential at national layer cases per hour
          </Text>
        </View>
      </View>

      <View style={styles.card}>
        <View style={styles.cardHeaderRow}>
          <View>
            <Text style={styles.cardTitle}>Max Potential Execution</Text>
            <Text style={styles.cardSubTitle}>
              Build location view · expand a build location to see linked cross dock sites · SKU slots from iCAM survey, volume from Ortec pre-shift
            </Text>
          </View>
          <ExportButton testID="order-rules-export-max-potential" onExport={onExport} />
        </View>

        {loading ? (
          <TableSkeleton rows={10} />
        ) : rows.length === 0 ? (
          <View style={styles.emptyStateWrap}>
            <Text style={styles.emptyStateText}>
              {showSitesWithoutLayerLine
                ? 'No sites for the current selection'
                : 'No build locations for the current selection'}
            </Text>
          </View>
        ) : (
          <View>
            {/* Sticky header — lives outside the fixed-height scrollable body */}
            <ScrollView
              ref={headerScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              onScroll={onHeaderHorizontalScroll}
              bounces={false}
              contentContainerStyle={maxPotentialContentWidthStyle}
            >
              <View style={styles.tableHeaderRow}>
                {maxPotentialExecutionColumns.map((column) => {
                  const isActive = maxSort.col === column.key;
                  return (
                    <TouchableOpacity
                      key={column.key}
                      style={[
                        styles.tableHeaderCell,
                        styles.maxPotentialHeaderCell,
                        {
                          width: column.minWidth ?? 120,
                          minWidth: column.minWidth ?? 120,
                          maxWidth: column.minWidth ?? 120,
                        },
                        column.align === 'right' && styles.tableHeaderCellAlignRight,
                        column.align === 'center' && styles.tableHeaderCellAlignCenter,
                      ]}
                      onPress={() => handleMaxSort(column.key)}
                      activeOpacity={0.7}
                      accessibilityLabel={`Sort by ${column.label}`}
                    >
                      <View style={[styles.maxPotentialHeaderTextWrap, { width: undefined, flex: 1 }]}>
                        <Text style={[styles.tableHeaderText, styles.maxPotentialHeaderText]} numberOfLines={2}>
                          {column.label}
                        </Text>
                        {maxPotentialHeaderTooltips[column.key] ? (
                          <DLOTooltip content={maxPotentialHeaderTooltips[column.key]} trigger="hover" position="top" maxWidth={300}>
                            <View style={styles.maxPotentialHeaderInfoIcon}>
                              <MaterialIcons name="info-outline" size={12} color="#818181" />
                            </View>
                          </DLOTooltip>
                        ) : null}
                      </View>
                      <MaterialIcons
                        name={isActive ? (maxSort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                        size={14}
                        color={isActive ? '#005CBC' : '#9E9E9E'}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>

            {/* Fixed-height body — only this box scrolls vertically, header stays put */}
            <View style={{ height: maxPotentialBodyHeight, overflow: 'hidden' }}>
              <ScrollView
                ref={bodyScrollRef}
                horizontal
                showsHorizontalScrollIndicator
                scrollEventThrottle={16}
                onScroll={onBodyHorizontalScroll}
                bounces={false}
                contentContainerStyle={maxPotentialContentWidthStyle}
              >
                <FlatList
                  data={sortedRows}
                  renderItem={renderMaxPotentialRow}
                  keyExtractor={(row) => row.key}
                  getItemLayout={getMaxPotentialItemLayout}
                  showsVerticalScrollIndicator
                  removeClippedSubviews
                  maxToRenderPerBatch={20}
                  windowSize={5}
                  initialNumToRender={15}
                />
              </ScrollView>
            </View>
          </View>
        )}
        {!loading && rows.length > 0 ? pagination : null}
      </View>
    </View>
  );
};

export default MaxPotentialTab;
